// Tier 3 labour roll-up for the Billing tab's Time Card Approval view.
//
// Strike's labour codes are a four-tier hierarchy, transcribed from the
// "Attendance & Force Report - Master" workbook (Labor Codes + Labor Rates
// tabs) that the field uses to build the client's Daily Force Report:
//
//   Tier 1  Category            Administration / Mechanical / Electrical
//   Tier 2  Category            the rate sheet's sections (Project Controls,
//                               Facility Construction, ...) — see
//                               billingRateSchedule.js
//   Tier 3  Daily Reporting     the 19 buckets the client's manpower report
//           Classification      counts by (Foreman, Journeymen, Welder, HSSE,
//                               ...) — what this module exists to resolve
//   Tier 4  Classification      the rate sheet's billable classifications
//                               (Foreman - Craft, Craftsman - A, ...)
//
// Tier 3 is the level the client sees. A daily labour report rolls every
// person on the job into one of these 19 rows, split Direct / Indirect.
//
// NOTE ON RATES: the workbook's Labor Rates tab also carries ST/OT rates, but
// they are the OLD 1.693-multiplier schedule. Rates come from
// billingRateSchedule.js (the current 2026.08.31 sheet, multiplier 1.633) —
// only the tier structure is taken from the workbook.

// Direct crafts are the 2000s, indirect/staff the 2100s. The split matches
// the two columns of the client's Daily Force Report.
export const TIER3 = [
    { code: 2000, name: 'Superintendents', dir: 'Direct' },
    { code: 2010, name: 'General Foreman', dir: 'Direct' },
    { code: 2020, name: 'Foreman', dir: 'Direct' },
    { code: 2030, name: 'Journeymen', dir: 'Direct' },
    { code: 2040, name: 'Helper', dir: 'Direct' },
    { code: 2050, name: 'Welder', dir: 'Direct' },
    { code: 2060, name: 'Operator', dir: 'Direct' },
    { code: 2070, name: 'Labor/FW/HW', dir: 'Direct' },
    { code: 2080, name: 'Apprentice', dir: 'Direct' },
    { code: 2100, name: 'Project Director/Manager', dir: 'Indirect' },
    { code: 2110, name: 'Construction Manager/APM', dir: 'Indirect' },
    { code: 2120, name: 'Project Controls/Scheduler', dir: 'Indirect' },
    { code: 2130, name: 'Field Engineer (Project Controls)', dir: 'Indirect' },
    { code: 2140, name: 'HSSE', dir: 'Indirect' },
    { code: 2150, name: 'QA \\ QC Lead', dir: 'Indirect' },
    { code: 2160, name: 'QA \\ QC Representative', dir: 'Indirect' },
    { code: 2170, name: 'Office Manager/Clerical', dir: 'Indirect' },
    { code: 2180, name: 'Material Manager', dir: 'Indirect' },
    { code: 2190, name: 'Material Expeditor \\ Tool Handler', dir: 'Indirect' },
];

export const tier3ByCode = TIER3.reduce((a, t) => { a[t.code] = t; return a; }, {});

// Rate-sheet (Tier 4) classification → Tier 3 code. Every classification on
// the rate schedule needs an entry here or its hours land in "Unclassified".
export const TIER4_TIER3 = {
    "Sr. Quality Manager": 2150,
    "Quality Manager": 2150,
    "Quality Control Inspector I": 2160,
    "Quality Control Inspector II": 2160,
    "Quality Control Inspector III": 2160,
    "Quality Control Inspector IV": 2160,
    "Safety Manager": 2140,
    "Safety Specialist": 2140,
    "HSE Specialist": 2140,
    "First Aid & Medical Support": 2140,
    "Sr. Program Manager": 2100,
    "Program Manager": 2100,
    "Assistant Program Manager": 2100,
    "Program Specialist": 2120,
    "Sr. Program Coordinator": 2120,
    "Program Coordinator": 2120,
    "Project Director": 2100,
    "Senior Project Manager": 2100,
    "Project Manager": 2100,
    "Assistant Project Manager": 2110,
    "Construction Manager": 2110,
    "Project Controls Manager": 2120,
    "Project Coordinator": 2120,
    "Sr. Planner": 2120,
    "Planner": 2120,
    "Sr. Cost Controller": 2120,
    "Cost Controller": 2120,
    "Project Admin Support Specialist": 2170,
    "Scheduling Manager": 2120,
    "Project Scheduler": 2120,
    "Procurement Manager": 2180,
    "Procurement Specialist": 2180,
    "Project Field Materials Coordinator": 2190,
    "Supply Chain Manager": 2180,
    "Material Buyer/Expeditor": 2190,
    "Material Receiving Clerk": 2190,
    "Document Control Manager": 2170,
    "Document Control Specialist": 2170,
    "IT and Communications Support": 2170,
    "Office Manager": 2170,
    "Superintendent - General": 2000,
    "Millwright Superintendent": 2000,
    "Ironworker Superintendent": 2000,
    "Piping Superintendent": 2000,
    "Rigging Superintendent": 2000,
    "Assistant Superintendent": 2000,
    "Foreman - General": 2010,
    "Foreman - Craft": 2020,
    "Foreman - Labor": 2020,
    "Foreman - Welding (w/o Rig)": 2020,
    "Welder - In-Service (w/o Rig)": 2050,
    "TIG Welder - Certified (w/o Rig)": 2050,
    "Welder - Certified (w/o Rig)": 2050,
    "Heavy Crane Operator": 2060,
    "Crane Operator": 2060,
    "Rigger": 2060,
    "Equipment Operator - A": 2060,
    "Equipment Operator - B": 2060,
    "Equipment Operator - C": 2060,
    "Truck Driver - A (Equipment Operator w/ CDL)": 2060,
    "Truck Driver - B": 2060,
    "Millwright": 2030,
    "Ironworker": 2030,
    "Craftsman - A": 2040,
    "Craftsman - B": 2040,
    "Craftsman - C": 2040,
    "Craft Helper - A": 2040,
    "Craft Helper - B": 2040,
    "Skilled Laborer": 2070,
    "Common Laborer": 2070,
    "Bending Engineer": 2040,
    "Mechanic": 2060,
    "Mechanic Helper": 2040,
    "Line Locator": 2070,
    "I&E Project Manager": 2100,
    "Electrical Construction Manager": 2110,
    "I&E Superintendent": 2000,
    "I&E Foreman": 2020,
    "Journeyman Electrician": 2030,
    "Electrician Apprentice 3rd / 4th year": 2080,
    "Electrician Apprentice 1st / 2nd year": 2080,
    "Instrument Fitter": 2030,
    "Instrument Fitter Helper": 2080,
    "Instrument Technician": 2030,
    "Electrical Equipment Operator": 2060,
    "Field Engineer I": 2130,
    "Field Engineer II": 2130,
    "Field Engineer III": 2130,
    "Mechanical Project Engineer": 2130,
    "Electrical Project Engineer": 2130,
    "I&E Specialist": 2130,
    // Added by the 2026.08.31 rate sheet, after this workbook was built:
    // Site Manager sits above Superintendent - General on wage, so it reports
    // as a superintendent; Field Engineer IV follows I/II/III.
    'Site Manager': 2000,
    'Field Engineer IV': 2130,
};

// JDE/HEET staff title → rate-sheet (Tier 4) classification, from the
// workbook's Labor Codes tab (columns J/K/L).
export const STAFF_TITLE_TIER4 = {
    "Assembly Technician": "Craftsman - B",
    "Assembly Technician (PKT)": "Craftsman - B",
    "Assistant Project Manager I": "Assistant Project Manager",
    "Assistant Project Manager II": "Assistant Project Manager",
    "Assistant Project Manager III": "Assistant Project Manager",
    "Assistant Superintendent": "Assistant Superintendent",
    "Assistant Superintendent (S)": "Assistant Superintendent",
    "Blaster": "Craftsman - B",
    "Coating & Painting Foreman": "Foreman - Craft",
    "Common Labor": "Common Laborer",
    "Construction Manager": "Construction Manager",
    "Craft Foreman": "Foreman - Craft",
    "Craft Helper": "Craft Helper - A",
    "Craft Helper 4": "Craft Helper - B",
    "Craftsman": "Craftsman - A",
    "Craftsman A": "Craftsman - A",
    "Document Control Analyst II": "Document Control Specialist",
    "Document Controls Spec III": "Document Control Specialist",
    "Document Controls Specialist": "Document Control Specialist",
    "Drill Foreman": "Foreman - Craft",
    "E&I Engineer V": "Electrical Project Engineer",
    "Electrical Journeyman I": "Journeyman Electrician",
    "Electrical Superintendent": "I&E Superintendent",
    "Equipment Operator": "Equipment Operator - A",
    "Equipment Operator 1": "Equipment Operator - A",
    "Equipment Operator 3": "Equipment Operator - C",
    "Equipment Operator III, Crane": "Crane Operator",
    "Field Service Technician I": "Mechanic",
    "Foreman": "Foreman - Craft",
    "Foreman (Hourly)": "Foreman - Craft",
    "General Foreman": "Foreman - General",
    "General Foreman (Hourly)": "Foreman - General",
    "General Superintendent": "Superintendent - General",
    "General Superintendent (S)": "Superintendent - General",
    "HSE Administrator": "Safety Specialist",
    "HSE Manager": "Safety Manager",
    "HSE Manager, OQ & Training": "Safety Manager",
    "HSE Program Administrator": "Safety Specialist",
    "HSE Representative II": "Safety Specialist",
    "HSE Specialist 2": "HSE Specialist",
    "HSE Specialist I": "HSE Specialist",
    "HSE Specialist II": "HSE Specialist",
    "HSE Specialist III": "HSE Specialist",
    "HSE Trainer": "Safety Specialist",
    "HSE Trainer III": "Safety Specialist",
    "Helper I": "Craft Helper - A",
    "IE Apprentice (2nd Year)": "Electrician Apprentice 1st / 2nd year",
    "IE Apprentice (3rd Year)": "Electrician Apprentice 3rd / 4th year",
    "Instrument Technician": "Instrument Technician",
    "Instrument Technician (PKT)": "Instrument Technician",
    "Lead Craftsman": "Craftsman - A",
    "Lead Project Support Spec.": "Project Admin Support Specialist",
    "Material Buyer/Expeditor": "Material Buyer/Expeditor",
    "Material Coordinator": "Project Field Materials Coordinator",
    "Mechanic": "Mechanic",
    "Mechanic Helper": "Mechanic Helper",
    "Mechanical Engineer": "Mechanical Project Engineer",
    "Office Manager": "Office Manager",
    "Office Manager (Hourly)": "Office Manager",
    "Painter": "Craftsman - B",
    "Painter (PKT)": "Craftsman - B",
    "Painter/Blaster": "Craftsman - B",
    "Pipe Bender": "Bending Engineer",
    "Pipe Fitter": "Craftsman - A",
    "Pipe Welder": "Welder - Certified (w/o Rig)",
    "Pipe Welder (PKT)": "Welder - Certified (w/o Rig)",
    "Procurement Manager": "Procurement Manager",
    "Procurement Specialist I": "Procurement Specialist",
    "Procurement Specialist II": "Procurement Specialist",
    "Program Manager": "Program Manager",
    "Project Admin Assistant": "Project Admin Support Specialist",
    "Project Admin Support": "Project Admin Support Specialist",
    "Project Controls Advisor": "Sr. Cost Controller",
    "Project Controls Analyst": "Cost Controller",
    "Project Controls Manager": "Project Controls Manager",
    "Project Controls Specialist I": "Cost Controller",
    "Project Controls Specialist II": "Cost Controller",
    "Project Director": "Project Director",
    "Project Manager": "Project Manager",
    "Project Support Assistant I": "Project Admin Support Specialist",
    "Project Support Specialist II": "Project Admin Support Specialist",
    "QA/QC Assistant": "Quality Control Inspector IV",
    "QA/QC Lead": "Quality Control Inspector I",
    "QA/QC Manager": "Quality Manager",
    "QA/QC Manager (PKT)": "Quality Manager",
    "QA/QC Specialist": "Quality Control Inspector II",
    "Quality Manager": "Quality Manager",
    "Regional Quality Manager Gulf": "Quality Manager",
    "Rigger": "Rigger",
    "Scheduler": "Project Scheduler",
    "Senior HSE Specialist": "HSE Specialist",
    "Senior Office Manager": "Office Manager",
    "Senior Procurement Specialist": "Procurement Specialist",
    "Senior Project Manager": "Senior Project Manager",
    "Ship/Rec Specialist": "Material Receiving Clerk",
    "Shipping/Receiving Specialist": "Material Receiving Clerk",
    "Skilled Labor": "Skilled Laborer",
    "Spacer": "Skilled Laborer",
    "Spotter": "Common Laborer",
    "Sr. Equipment Operator-Crane": "Heavy Crane Operator",
    "Sr. Manager, Quality Assurance": "Sr. Quality Manager",
    "Sr. Mechanical Engineer": "Mechanical Project Engineer",
    "Sr. Pipe Welder": "Welder - Certified (w/o Rig)",
    "Sr. Project Admin Support": "Project Admin Support Specialist",
    "Sr. Project Support Specialist": "Project Admin Support Specialist",
    "Strawboss": "Foreman - Labor",
    "Structural Fab Superintendent": "Ironworker Superintendent",
    "Structural Welder (PKT)": "Welder - In-Service (w/o Rig)",
    "Superintendent": "Superintendent - General",
    "Superintendent (Hourly)": "Superintendent - General",
    "Supply Chain Manager": "Supply Chain Manager",
    "Tie-In Foreman": "Foreman - Craft",
    "Truck Driver": "Truck Driver - B",
    "Warehouse Manager": "Project Field Materials Coordinator",
    "Welder": "Welder - In-Service (w/o Rig)",
    "Welder - (w/o Rig)": "Welder - In-Service (w/o Rig)",
    "Welder - (w/o Rig) (PKT)": "Welder - In-Service (w/o Rig)",
    "Welder - Certified (w/o rig)": "Welder - Certified (w/o Rig)",
    "Welder Helper": "Craft Helper - A",
    "Welding Foreman": "Foreman - Welding (w/o Rig)",
};

// Titles the workbook explicitly marks "N/A" — corporate and back-office
// roles that are not field labour and carry no billable classification.
// Listed so the view can say "not field labour" instead of "unrecognised".
export const NON_FIELD_TITLES = new Set([
    "AI Analyst",
    "AR / Collections Manager",
    "AR Supervisor",
    "Accounting Manager",
    "Accounts Payable Clerk",
    "Accounts Payable Manager",
    "Accounts Payable Specialist I",
    "Accounts Payable Specialist II",
    "Accounts Receivable Specialist",
    "Applications Manager",
    "BI Analyst III",
    "Bid Coordinator",
    "Bid Manager",
    "Billing & Invoicing Coord. II",
    "Business Applications Manager",
    "Business Intelligence Manager",
    "Chief Executive Officer",
    "Chief Financial Officer",
    "Chief Human Resources Officer",
    "Chief Information Officer",
    "Civil Designer II",
    "Collections Specialist",
    "Construction App. Analyst",
    "Corporate Counsel",
    "Corrosion Technician I",
    "Corrosion Technician II",
    "Data Engineer",
    "Developer",
    "Director",
    "Director of Construction",
    "Director of Construction, Pipe",
    "Director, Business Development",
    "Director, Commercial Controls",
    "Director, E&I",
    "Director, Facilities",
    "Director, Gulf Coast Region",
    "Director, HSE",
    "Director, IT Systems Analysis",
    "Director, Northern Region",
    "Director, Pickett Systems",
    "Director, Pipeline",
    "Director, Project Controls",
    "Director, Project Estimating",
    "Director, Project Management",
    "Director, Risk & Ins. Mgmt",
    "Division Lead, Piping",
    "Engineering Manager",
    "Engineering Operations Manager",
    "Estimating Manager",
    "Executive Assistant",
    "Fleet Administrative Support",
    "HR Business Partner",
    "HR Manager, Talent Acquisition",
    "HSE Training Supervisor",
    "Human Resources Assistant",
    "Human Resources Generalist II",
    "Human Resources Specialist",
    "I&E Estimating Manager",
    "IT Systems Analysis Supervisor",
    "JD Edwards PTP Support Lead",
    "Lead Designer",
    "Lead Electrical Designer",
    "Lead, App Support Analyst",
    "Manager, AI Strategy",
    "Manager, Business Development",
    "Marketing Coordinator II",
    "Marketing Supervisor",
    "Mgr. HSE Programs & Compliance",
    "Office Services Professional",
    "Office Services Specialist",
    "Operations Manager",
    "Payroll Analyst",
    "Payroll Manager",
    "Piping Designer",
    "Project Estimator",
    "Project Estimator I",
    "Project Estimator III",
    "Project Lifecycle Process Mngr",
    "Regional Fleet Manager",
    "SVP Regional Ops & Services",
    "SVP, Business Development",
    "SVP, Region Operations",
    "Sales & Source Analyst III",
    "Senior AR Specialist",
    "Senior Business Analyst",
    "Senior Database Developer",
    "Senior Executive Assistant",
    "Senior Financial Analyst",
    "Senior Fleet Specialist",
    "Senior Project Estimator",
    "Sr. Accountant",
    "Sr. Director, Supply Chain/QA",
    "Sr. Manager, Estimating",
    "Sr. Manager, Human Resources",
    "Sr. Manager, Operations",
    "Sr. Manager, Technical Sales",
    "Sr. Mgr, Business Development",
    "Sr. Operations Manager",
    "Talent Acquisition Spec II",
    "Talent Coordinator III",
    "Talent Development Manager",
    "VP, Engineering & Capital",
    "VP, Industry Operations",
    "Vice President",
    "Vice President & Controller",
    "Vice President, HSE",
    "Vice President, Legal",
]);

// Craft titles that appear in HEET but have no unambiguous Tier 4 rate — the
// reporting bucket is obvious even where the rate would be a guess. Keyed on
// the normalised title (see normClass in billingLaborMap.js).
export const TIER3_ALIASES = {
    'welder smaw': 2050,
    'welder w rig': 2050,
    'weld foreman': 2020,
    'senior foreman': 2020,
    laborer: 2070,
    'experienced laborer': 2070,
    helper: 2040,
    'craftsman 3 a': 2040,
    'craftsman 2 b': 2040,
    pipefitter: 2040,
    'painter sandblaster': 2040,
    'superintendent a': 2000,
    operator: 2060,
    'equipment operator 4': 2060,
    'heavy equipment operator': 2060,
    'cdl driver': 2060,
    'electrical journeyman ii': 2030,
    'ie apprentice 1st year': 2080,
    'ie apprentice 2nd year': 2080,
    'procurement spec ii': 2180,
    'hse trainer i': 2140,
    'qa qc certified welding inspec': 2160,
    'sr project manager': 2100,
    'project coordinator i': 2120,
    'office services specialist': 2170,
    'office services professional': 2170,
    'administrative assistant': 2170,
    'equipment operator truck dri': 2060,
};

// ── Resolution ──
// A HEET pay class resolves to a Tier 3 bucket through, in order: the job's
// own saved JDE→classification map (the Rates & Rules card), an exact match
// on a rate-sheet classification, the workbook's staff-title map, a
// truncation-tolerant match (JDE cuts classification names at 30 chars), and
// finally the Tier 3 alias table.

import { normClass } from './billingLaborMap';

const t4ByNorm = {};
Object.keys(TIER4_TIER3).forEach((n) => { const k = normClass(n); if (!t4ByNorm[k]) t4ByNorm[k] = n; });

const titleByNorm = {};
Object.keys(STAFF_TITLE_TIER4).forEach((t) => {
    const k = normClass(t);
    if (!titleByNorm[k]) titleByNorm[k] = STAFF_TITLE_TIER4[t];
});

const nonFieldByNorm = new Set();
NON_FIELD_TITLES.forEach((t) => nonFieldByNorm.add(normClass(t)));

// JDE stores Labor_Classification_Description in 30 characters and silently
// chops anything longer ("Project Admin Support Speciali"), so a normalised
// name that is a prefix of exactly one rate-sheet classification is that
// classification. Requires 12+ characters so short names can't collide.
const truncatedMatch = (key) => {
    if (key.length < 12) return '';
    const hits = Object.keys(t4ByNorm).filter((k) => k.startsWith(key));
    return hits.length === 1 ? t4ByNorm[hits[0]] : '';
};

// reason: how the bucket was found — 'map' (job override), 'exact', 'title',
// 'truncated', 'alias', 'non-field' (corporate role, no billable class) or
// 'unknown'. The view surfaces the last two so nothing is silently dropped.
export const resolveTier3 = (payClass, ratemap) => {
    const raw = String(payClass || '').trim();
    if (!raw) return { t3: null, cls: '', reason: 'unknown' };
    const key = normClass(raw);

    // A stored mapping always wins, including an explicit blank ("unmapped").
    const saved = ratemap && Object.prototype.hasOwnProperty.call(ratemap, raw) ? ratemap[raw] : undefined;
    if (saved !== undefined) {
        if (!saved) return { t3: null, cls: '', reason: 'unknown' };
        return { t3: tier3ByCode[TIER4_TIER3[saved]] || null, cls: saved, reason: 'map' };
    }

    if (t4ByNorm[key]) {
        const cls = t4ByNorm[key];
        return { t3: tier3ByCode[TIER4_TIER3[cls]] || null, cls, reason: 'exact' };
    }
    if (titleByNorm[key]) {
        const cls = titleByNorm[key];
        return { t3: tier3ByCode[TIER4_TIER3[cls]] || null, cls, reason: 'title' };
    }
    const trunc = truncatedMatch(key);
    if (trunc) return { t3: tier3ByCode[TIER4_TIER3[trunc]] || null, cls: trunc, reason: 'truncated' };

    if (TIER3_ALIASES[key]) return { t3: tier3ByCode[TIER3_ALIASES[key]] || null, cls: '', reason: 'alias' };
    if (nonFieldByNorm.has(key)) return { t3: null, cls: '', reason: 'non-field' };
    return { t3: null, cls: '', reason: 'unknown' };
};
